import React, { useEffect, useMemo, useState } from "react";
import { Bed, ChevronDown, ChevronLeft, ChevronRight, SlidersHorizontal, User } from "lucide-react";
import { roomService } from "../../services/room.service";
import { roomSliderImages } from "../HomePage/sections/roomSliderImages";
import styles from "./RoomRatesPage.module.css";

const BOOK_URL = "https://book-directonline.com/properties/southtweedmidirect";

const toDateInput = (date) => {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

const parseLocalDate = (value) => {
  const dateOnly = String(value || "").slice(0, 10);
  const [year, month, day] = dateOnly.split("-").map(Number);
  if (!year || !month || !day) return new Date();
  return new Date(year, month - 1, day);
};

const formatStayDate = (value) =>
  parseLocalDate(value).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });

const imagesForName = (name = "") => {
  const label = name.toLowerCase();
  if (label.includes("twin")) return roomSliderImages.deluxeTwin;
  if (label.includes("family") && (label.includes("2") || label.includes("two"))) {
    return roomSliderImages.familyRoom2;
  }
  if (label.includes("family")) return roomSliderImages.familyRoom4;
  if (label.includes("deluxe")) return roomSliderImages.deluxeRoom;
  if (label.includes("queen")) return roomSliderImages.queenRoom;
  return roomSliderImages.queenRoom;
};

const LOCAL_ROOMS = [
  {
    _id: "queen-room",
    name: "Queen Room",
    description:
      "Standard queen room, air conditioned/heating, microwave, toaster, bar fridge, tea and coffee facilities, fresh milk, and daily room service.",
    images: roomSliderImages.queenRoom,
    bedType: "Queen",
    maxOccupancy: 2,
    amenities: ["Free Wi-Fi", "Parking", "Swimming Pool", "Non-Smoking"],
    pricePerNight: 120,
  },
  {
    _id: "deluxe-room",
    name: "Deluxe Room",
    description:
      "Our Deluxe Room offers air conditioned/heating, microwave, toaster, bar fridge, tea and coffee facilities, fresh milk, and daily room service.",
    images: roomSliderImages.deluxeRoom,
    bedType: "Queen",
    maxOccupancy: 2,
    amenities: ["Free Wi-Fi", "Parking", "Swimming Pool", "Non-Smoking"],
    pricePerNight: 180,
  },
  {
    _id: "deluxe-twin",
    name: "Deluxe Twin",
    description:
      "Standard Twin Room, one queen bed and one single bed, air conditioned/heating, microwave, toaster, bar fridge, tea and coffee facilities, fresh milk, daily room service, and free Wi-Fi.",
    images: roomSliderImages.deluxeTwin,
    bedType: "Queen + Single",
    maxOccupancy: 3,
    amenities: ["Free Wi-Fi", "Parking", "Swimming Pool", "Non-Smoking"],
    pricePerNight: 220,
  },
  {
    _id: "family-room",
    name: "Family Room (sleeps 4)",
    description:
      "Standard family room with a double bed, one single, and a set of bunk beds. Air conditioned/heating, microwave, toaster, bar fridge, tea and coffee facilities, fresh milk, daily room service, and free Wi-Fi.",
    images: roomSliderImages.familyRoom4,
    bedType: "Double + Single + Bunks",
    maxOccupancy: 4,
    amenities: ["Free Wi-Fi", "Parking", "Swimming Pool", "Non-Smoking"],
  },
  {
    _id: "family-room-2",
    name: "Family Room (sleeps 2)",
    description:
      "Standard family room with a double bed and two single beds. Air conditioned/heating, microwave, toaster, bar fridge, tea and coffee facilities, fresh milk, daily room service, and free Wi-Fi.",
    images: roomSliderImages.familyRoom2,
    bedType: "Double + Singles",
    maxOccupancy: 2,
    amenities: ["Free Wi-Fi", "Parking", "Swimming Pool", "Non-Smoking"],
  },
];

const resolveImages = (room) => {
  const fromRoom = (room.images || []).filter(
    (src) => typeof src === "string" && src && !src.includes("placeholder")
  );
  if (fromRoom.length) return fromRoom;
  return imagesForName(room.name);
};

const nightlyPrice = (room, nights) => {
  const bestRate = room.rateOptions?.[0];
  if (bestRate?.nightlyPrice) return Number(bestRate.nightlyPrice);
  if (bestRate?.totalPrice && nights) {
    return Math.round(Number(bestRate.totalPrice) / nights);
  }
  if (room.basePrice) return Number(room.basePrice);
  if (room.pricePerNight) return Number(room.pricePerNight);
  return null;
};

const rateRowsFor = (room, nights, selectedRate) => {
  if (room.rateOptions?.length) {
    return room.rateOptions.map((rate, index) => ({
      id: rate._id || `${room._id}-rate-${index}`,
      name: rate.name || selectedRate || "Lowest Regular Rate",
      detail: rate.discountText || "Room only",
      price: rate.totalPrice
        ? Math.round(Number(rate.totalPrice) / nights)
        : nightlyPrice(room, nights),
    }));
  }

  const price = nightlyPrice(room, nights);
  if (price == null) return [];

  return [
    {
      id: `${room._id}-regular`,
      name: selectedRate || "Lowest Regular Rate",
      detail: "Room only",
      price,
    },
  ];
};

const RoomRatesPage = () => {
  const stay = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    return {
      checkIn: params.get("checkIn") || toDateInput(today),
      checkOut: params.get("checkOut") || toDateInput(tomorrow),
      rooms: Number(params.get("rooms") || 1),
      adults: Number(params.get("adults") || 1),
      children: Number(params.get("children") || 0),
      rate: params.get("rate") || "Lowest Regular Rate",
    };
  }, []);

  const nights = Math.max(
    1,
    Math.round(
      (parseLocalDate(stay.checkOut) - parseLocalDate(stay.checkIn)) / 86400000
    )
  );

  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imageIndexes, setImageIndexes] = useState({});
  const [openRates, setOpenRates] = useState({});
  const [openDetails, setOpenDetails] = useState({});
  const [showTax, setShowTax] = useState(false);

  useEffect(() => {
    const loadRooms = async () => {
      try {
        const res = await roomService.searchRooms({
          checkIn: stay.checkIn,
          checkOut: stay.checkOut,
          adults: stay.adults,
          children: stay.children,
        });

        if (res.success && Array.isArray(res.data) && res.data.length) {
          setRooms(
            res.data.map((room) => ({
              ...room,
              images: resolveImages(room),
            }))
          );
          return;
        }
      } catch (error) {
        console.error("Failed to load rooms", error);
      } finally {
        setLoading(false);
      }

      setRooms(LOCAL_ROOMS);
      setLoading(false);
    };

    loadRooms();
  }, [stay]);

  const showPrice = (amount) => {
    if (amount == null || Number.isNaN(amount)) return null;
    const value = showTax ? Math.round(amount * 1.1) : amount;
    return value.toLocaleString("en-AU");
  };

  const shiftImage = (roomId, total, direction) => {
    setImageIndexes((prev) => ({
      ...prev,
      [roomId]: ((prev[roomId] || 0) + direction + total) % total,
    }));
  };

  const guestLabel = `${stay.rooms} ${stay.rooms === 1 ? "Room" : "Rooms"}, ${
    stay.adults
  } ${stay.adults === 1 ? "Adult" : "Adults"}${
    stay.children > 0
      ? `, ${stay.children} ${stay.children === 1 ? "Child" : "Children"}`
      : ""
  }`;

  if (loading) {
    return <div className={styles.loading}>Loading rooms...</div>;
  }

  return (
    <div className={styles.page}>
      <div className={styles.summaryBar}>
        <div>
          <span>STAY DATES ({nights} {nights === 1 ? "NIGHT" : "NIGHTS"})</span>
          <strong>
            {formatStayDate(stay.checkIn)} – {formatStayDate(stay.checkOut)}
          </strong>
        </div>
        <div>
          <span>ROOMS & GUESTS</span>
          <strong>{guestLabel}</strong>
        </div>
        <div>
          <span>SPECIAL RATES</span>
          <strong>{stay.rate}</strong>
        </div>
        <label className={styles.points}>
          <input type="checkbox" />
          <span>Use Points/Awards</span>
        </label>
      </div>

      <div className={styles.container}>
        <h1>Select a Room and Rate</h1>

        <div className={styles.filterRow}>
          <button type="button">
            <SlidersHorizontal size={17} />
            All Filters
          </button>
          <button type="button">
            Accessibility
            <ChevronDown size={17} />
          </button>
          <button type="button">
            Bed Type
            <ChevronDown size={17} />
          </button>
          <label className={styles.taxOption}>
            <input
              type="checkbox"
              checked={showTax}
              onChange={() => setShowTax((current) => !current)}
            />
            Show with taxes and fees
            <span>$ AUD</span>
          </label>
        </div>

        <div className={styles.roomsList}>
          {rooms.map((room) => {
            const images = resolveImages(room);
            const currentIndex = imageIndexes[room._id] || 0;
            const price = nightlyPrice(room, nights);
            const ratesOpen = Boolean(openRates[room._id]);
            const detailsOpen = Boolean(openDetails[room._id]);
            const rows = rateRowsFor(room, nights, stay.rate);
            const amenityList = (room.amenities || [])
              .map((item) => (typeof item === "string" ? item : item?.name))
              .filter(Boolean);

            return (
              <article className={styles.roomCard} key={room._id}>
                <div className={styles.roomMain}>
                  <div className={styles.imageBox}>
                    {images.length > 0 && (
                      <img src={images[currentIndex]} alt={room.name} />
                    )}

                    {images.length > 1 && (
                      <>
                        <button
                          type="button"
                          className={styles.leftArrow}
                          onClick={() => shiftImage(room._id, images.length, -1)}
                          aria-label={`Previous photo of ${room.name}`}
                        >
                          <ChevronLeft size={22} />
                        </button>
                        <button
                          type="button"
                          className={styles.rightArrow}
                          onClick={() => shiftImage(room._id, images.length, 1)}
                          aria-label={`Next photo of ${room.name}`}
                        >
                          <ChevronRight size={22} />
                        </button>
                        <div className={styles.dots}>
                          {images.map((_, index) => (
                            <button
                              key={index}
                              type="button"
                              className={
                                index === currentIndex ? styles.activeDot : styles.dot
                              }
                              onClick={() =>
                                setImageIndexes((prev) => ({
                                  ...prev,
                                  [room._id]: index,
                                }))
                              }
                              aria-label={`Show photo ${index + 1} of ${room.name}`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </div>

                  <div className={styles.roomContent}>
                    <div className={styles.roomTop}>
                      <h2>{room.name}</h2>
                      <button
                        type="button"
                        className={styles.details}
                        onClick={() =>
                          setOpenDetails((prev) => ({
                            ...prev,
                            [room._id]: !prev[room._id],
                          }))
                        }
                        aria-expanded={detailsOpen}
                      >
                        Room Details
                      </button>
                    </div>

                    {detailsOpen && room.description && (
                      <p className={styles.description}>{room.description}</p>
                    )}

                    <div className={styles.specs}>
                      {room.bedType && (
                        <span>
                          <Bed size={16} /> {room.bedType}
                        </span>
                      )}
                      {room.size && <span>{room.size} m²</span>}
                      {room.maxOccupancy && (
                        <span>
                          <User size={16} /> Max {room.maxOccupancy}
                        </span>
                      )}
                    </div>

                    {amenityList.length > 0 && (
                      <div className={styles.amenities}>
                        {amenityList.slice(0, 4).map((item) => (
                          <span key={item}>{item}</span>
                        ))}
                      </div>
                    )}

                    <div className={styles.roomBottom}>
                      <div className={styles.price}>
                        <span>Rates from</span>
                        {price != null ? (
                          <strong>
                            ${showPrice(price)}
                            <small>{showTax ? " incl. taxes" : " / Night"}</small>
                          </strong>
                        ) : (
                          <strong>See booking page</strong>
                        )}
                      </div>

                      <button
                        type="button"
                        className={styles.rateButton}
                        onClick={() =>
                          setOpenRates((prev) => ({
                            ...prev,
                            [room._id]: !prev[room._id],
                          }))
                        }
                        aria-expanded={ratesOpen}
                      >
                        {ratesOpen ? "− Hide Rates" : "+ View Rates"}
                      </button>
                    </div>
                  </div>
                </div>

                {ratesOpen && (
                  <div className={styles.ratesPanel}>
                    {rows.length === 0 ? (
                      <div className={styles.rateRow}>
                        <div>
                          <strong>{stay.rate}</strong>
                          <span>Confirm the nightly rate on Arabella’s booking page.</span>
                        </div>
                        <a
                          className={styles.selectBtn}
                          href={BOOK_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Select
                        </a>
                      </div>
                    ) : (
                      rows.map((rate) => (
                        <div className={styles.rateRow} key={rate.id}>
                          <div>
                            <strong>{rate.name}</strong>
                            <span>{rate.detail}</span>
                          </div>
                          <div className={styles.ratePrice}>
                            {rate.price != null && (
                              <>
                                <b>${showPrice(rate.price)}</b>
                                <small>{showTax ? "incl. taxes" : "/ Night"}</small>
                              </>
                            )}
                          </div>
                          <a
                            className={styles.selectBtn}
                            href={BOOK_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Select
                          </a>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default RoomRatesPage;
